import * as v from 'valibot'
import { createEvent } from 'effector'
import { F } from '@mobily/ts-belt'
import { format, logger } from './lib'
import { PERSIST_REQUEST_POLICY_BUCKETS_DEBOUNCE } from './index.h'
import { debugPrintColor } from './colors'

const BucketSchema = v.object({
  limit: v.number(), // Timestamp, requests in this group are prohibited until this time
  failures: v.array(v.number()), // Array of timestamps when requests in this group failed
  backoff: v.number(), // Last calculated backoff timeout duration in milliseconds
  successes: v.number(), // Counter of successful requests after failures occurred
})

const BlockedBucketsSchema = v.pipe(
  v.record(
    v.string(), // Request group identifier for batching similar requests
    BucketSchema
  ),
  v.transform((record) => new Map(Object.entries(record)))
)

type BlockedBucket = v.InferOutput<typeof BucketSchema>
type BlockedBucketsMap = v.InferOutput<typeof BlockedBucketsSchema>
type BlockedBucketsMapIterator = ReturnType<BlockedBucketsMap['entries']>

const load = getFromStorage
const save = F.debounce(setToStorage, PERSIST_REQUEST_POLICY_BUCKETS_DEBOUNCE)

export const onBucketsUpdate = createEvent()

export class RequestBuckets {
  public map: BlockedBucketsMap

  constructor(public persistKey?: string) {
    this.map = load(persistKey) ?? new Map()

    // save buckets on 'beforeunload' event
    // this is required because `this.save` is debounced, and may loose recent data
    addEventListener('beforeunload', () => {
      setToStorage(this.persistKey, this.map)
    })
  }

  isEmpty(): boolean {
    return this.map.size === 0
  }

  all(): BlockedBucketsMapIterator {
    return this.map.entries()
  }

  get(bucketName?: string): BlockedBucket | undefined {
    if (!bucketName) return
    return this.map.get(bucketName)
  }

  set(bucketName?: string, bucket?: BlockedBucket): void {
    if (!bucketName || !bucket) return
    this.map.set(bucketName, bucket)
    save(this.persistKey, this.map)
    onBucketsUpdate()
  }

  delete(bucketName?: string): boolean {
    if (!bucketName) return false
    const result = this.map.delete(bucketName)
    save(this.persistKey, this.map)
    onBucketsUpdate()
    return result
  }

  clear(): void {
    this.map.clear()
    save(this.persistKey, this.map)
    onBucketsUpdate()
  }

  debugPrint(prefix = ''): void {
    if (this.isEmpty()) {
      logger.debug(`${prefix} %c~empty~`, 'color:gray')
      return
    }

    const now = Date.now()
    const buckets: string[] = []
    const styles: string[] = []
    for (const [name, bucket] of this.map.entries()) {
      const isExpired = bucket.limit < now
      const timestampStyles: string[] = []
      buckets.push(
        `%c${name}%c:
\tblocked until %c${format(bucket.limit)}${isExpired ? ' (expired)' : ''}%c
\tbackoff ${bucket.backoff}ms
\tsuccesses ${bucket.successes}
\tfailures: ${bucket.failures.length} [${bucket.failures
          .map((t) => {
            timestampStyles.push(`color:${debugPrintColor(t)}`, 'color:inherit') // color for timestamps
            return '%c' + format(t) + '%c'
          })
          .join(', ')}]`
      )
      // biome-ignore format: for better readability
      styles.push(
        'color:red', 'color:inherit', // color for bucket name
        isExpired ? 'color:gray' : 'color:orange', 'color:inherit', // color for limit time
        ...timestampStyles, // styles for timestamps
      )
    }
    logger.debug(`${prefix}\n${buckets.join('\n')}`, ...styles)
  }
}

function getFromStorage(key?: string): BlockedBucketsMap | undefined {
  if (key == null) return

  try {
    const saved = localStorage.getItem(key)
    if (saved) {
      const parsed = JSON.parse(saved)
      return v.parse(BlockedBucketsSchema, parsed)
    }
  } catch (e) {
    logger.warn('Failed to load request buckets from localStorage:', e)
  }
}

function setToStorage(key?: string, buckets?: BlockedBucketsMap): void {
  if (key == null || buckets == null) return

  try {
    if (buckets.size === 0) {
      localStorage.removeItem(key)
    } else {
      const record = Object.fromEntries(buckets)
      const stringified = JSON.stringify(record)
      localStorage.setItem(key, stringified)
    }
  } catch (e) {
    logger.warn('Failed to save request buckets to localStorage', e)
  }
}
